import { useConfigStore } from 'scbk-services/config/stores/configStore';
import { ConfigRegistry } from 'scbk-services/types/config/config-types';

import { useLocaleStore } from '../stores/localeStore';

export const useConfig = () => {
    const getEnv = useConfigStore((state) => state.getEnv);
    const getMarket = useConfigStore((state) => state.getMarket);
    const getStoreConfig = useConfigStore((state) => state.getConfig);
    const setConfig = useConfigStore((state) => state.setConfig);
    const getChannel = useConfigStore((state) => state.getChannel);
    const getDevice = useConfigStore((state) => state.getDevice);
    const getSystem = useConfigStore((state) => state.getSystem);
    const getLocale = useLocaleStore((state) => state.getLocale);

    const getConfig = <M extends keyof ConfigRegistry>(module: M) => {
        const configs = getStoreConfig();
        const currentMarket = getMarket();
        const currentEnv = getEnv();
        const currentChannel = getChannel();
        const currentDevice = getDevice();
        const currentSystem = getSystem();
        const currentLocale = getLocale();

        const moduleConfigs = configs[module];

        if (!moduleConfigs) return {};

        const processedConfigs = Object.fromEntries(
            Object.entries(moduleConfigs).map(([key, configValue]) => {
                const marketConfig =
                    configValue.markets?.[currentMarket as keyof typeof configValue.markets];

                const systemConfig =
                    configValue.system?.[currentSystem as keyof typeof configValue.system];

                const languageConfig =
                    marketConfig?.languages?.[currentLocale as keyof typeof marketConfig.languages];

                const value =
                    languageConfig?.env?.[currentEnv] ??
                    languageConfig?.value ??
                    marketConfig?.env?.[currentEnv] ??
                    marketConfig?.channel?.[currentChannel] ??
                    marketConfig?.devices?.[currentDevice] ??
                    marketConfig?.value ??
                    systemConfig?.env?.[currentEnv] ??
                    systemConfig?.value ??
                    configValue.env?.[currentEnv] ??
                    configValue.channel?.[currentChannel] ??
                    configValue.devices?.[currentDevice] ??
                    configValue.value;

                return [key, value];
            }),
        );

        return processedConfigs;
    };

    const getConfigByKey = <M extends keyof ConfigRegistry>(module: M, key: string) => {
        const currentConfigs = getConfig(module);
        return currentConfigs[key];
    };

    return {
        getConfigByKey,
        getConfig,
        setConfig,
    };
};

export default useConfig;
